import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import Cookies from 'js-cookie';
import { API_BASE_URL, VALIDATE_INVITE } from '../apiServices/api_routes';
import axiosInstance from '../apiServices/axios';
import { GetUserData, GetMyWorkspaces, setActiveWorkspaceId, setWorkspaceSelectionRequired } from '../redux/usersSlice';
import { GetProjectData, GetCurrentProjectData, selectProject } from '../redux/projectDataSlice';
import store from '../redux/store';
import { getProjectHomePath } from '../utils/projectHomePath';
import { stashPostRegisterValidateKey, clearRouteAfterLoginInviteLock } from '../utils/pendingInviteKey';
import InviteExpiredCard from '../components/InviteFlow/InviteExpiredCard';
import Logo from '../assets/longLogoBright.png';
import styles from '../components/Register/css/Register.module.css';
import axios from 'axios';

function getAccessTokenForApi() {
    const fromStore = store.getState()?.user?.access;
    if (fromStore) return fromStore;
    try {
        const raw = localStorage.getItem('persist:root');
        if (!raw) return '';
        const root = JSON.parse(raw);
        const userChunk = root.user;
        if (typeof userChunk === 'string') {
            const u = JSON.parse(userChunk);
            return u?.access || '';
        }
        if (userChunk && typeof userChunk === 'object') {
            return userChunk.access || '';
        }
    } catch {
        /* ignore */
    }
    return '';
}

const ValidateInvitePage = () => {
    const { key } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [screen, setScreen] = useState('loading');
    const [meta, setMeta] = useState(null);

    // Release Login.routeAfterLogin guard when leaving this screen (mount clears too early vs. duplicate effects).
    React.useEffect(() => {
        return () => clearRouteAfterLoginInviteLock();
    }, []);

    const landInProject = useCallback(
        async data => {
            const u = store.getState().user?.user;
            if (data.workspace_id) {
                dispatch(setActiveWorkspaceId(data.workspace_id));
            }
            dispatch(setWorkspaceSelectionRequired(false));
            if (data.project_id) {
                localStorage.setItem('project_id', data.project_id);
                try {
                    await dispatch(GetProjectData()).unwrap();
                    const projects = store.getState().projectData?.data;
                    const list = Array.isArray(projects) ? projects : [];
                    const currentProject = list.find(p => String(p.id) === String(data.project_id));
                    if (currentProject) {
                        dispatch(selectProject({ id: currentProject.id }));
                    }
                    try {
                        await dispatch(GetCurrentProjectData()).unwrap();
                    } catch {
                        /* detail fetch optional */
                    }
                } catch {
                    try {
                        await dispatch(GetCurrentProjectData()).unwrap();
                        dispatch(selectProject({ id: data.project_id }));
                    } catch {
                        /* fall through to project list */
                    }
                }
            }
            const proj = store.getState().projectData?.selectedProject;
            const path = getProjectHomePath(proj, u);
            navigate(path, { replace: true });
        },
        [dispatch, navigate],
    );

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (!key) {
                setScreen('unable');
                return;
            }
            setScreen('loading');
            try {
                const { data } = await axios.get(`${API_BASE_URL}${VALIDATE_INVITE}${encodeURIComponent(key)}/`);
                if (cancelled) return;
                if (!data?.valid) {
                    setScreen('unable');
                    return;
                }
                if (data.expired && !data.accepted) {
                    setMeta(data);
                    setScreen('expired');
                    return;
                }
                setMeta(data);

                const refresh = Cookies.get('refresh');
                if (!refresh) {
                    if (data.accepted || data.user_exists) {
                        navigate(`/login?invite=${encodeURIComponent(key)}`, { replace: true });
                        return;
                    }
                    stashPostRegisterValidateKey(key);
                    navigate(`/register/${key}`, { replace: true });
                    return;
                }

                try {
                    await dispatch(GetUserData()).unwrap();
                } catch {
                    /* session invalid */
                }
                if (cancelled) return;
                const user = store.getState().user?.user;
                const userEmail = (user?.email || '').trim().toLowerCase();
                const invited = (data.invited_email || '').trim().toLowerCase();
                if (!userEmail || !invited) {
                    setScreen('unable');
                    return;
                }
                if (userEmail !== invited) {
                    setScreen('unable');
                    return;
                }

                setScreen('working');
                try {
                    const token = getAccessTokenForApi();
                    await axiosInstance.post(
                        `accounts/invitations/accept-invite/${key}/`,
                        {},
                        {
                            headers: token ? { Authorization: `Bearer ${token}` } : {},
                        },
                    );
                } catch {
                    /* audit / duplicate accept */
                }
                // Invite login path skips GetMyWorkspaces on Login.routeAfterLogin; load memberships before protected routes.
                try {
                    await dispatch(GetMyWorkspaces()).unwrap();
                } catch {
                    /* still land using workspace_id from invite */
                }
                await landInProject(data);
            } catch {
                if (!cancelled) {
                    setScreen('unable');
                }
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [key, dispatch, navigate, landInProject]);

    if (screen === 'loading' || screen === 'working') {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    gap: '1rem',
                }}>
                <AiOutlineLoading3Quarters className="rotateLoader" style={{ fontSize: '2.5rem', color: 'var(--Brand-Primary)' }} />
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Verifying invitation…</p>
            </div>
        );
    }

    if (screen === 'unable') {
        return (
            <div className={styles.body}>
                <div className={styles.dotedBg}>
                    <div className={styles.formCard}>
                        <div className={styles.formHeader}>
                            <img src={Logo} width={73} alt="lens-logo" />
                        </div>
                        <div className={styles.formBody}>
                            <h5 className={styles.formLabel}>We&apos;re Unable to Verify This Invitation</h5>
                            <p className={styles.inviteGateLead}>
                                For security reasons, we&apos;re unable to validate this invitation link. This may happen if the link has been altered, was opened using a
                                different email account, or is no longer valid.
                                <br />
                                <br />
                                Please contact Lens Support for assistance.
                            </p>
                            <a
                                href="mailto:LensSupport@beyond-ma.com"
                                className={styles.submitBtn}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    textDecoration: 'none',
                                    boxSizing: 'border-box',
                                }}>
                                Contact Lens Support
                            </a>
                        </div>
                    </div>
                </div>
                <div className={styles.footer}>
                    <a href="https://www.iubenda.com/terms-and-conditions/15531073" target="_blank" rel="noreferrer">
                        Terms of Use
                    </a>
                    <a href="https://www.iubenda.com/privacy-policy/15531073/legal?ifr=true&height=650&newmarkup=yes&an=no" target="_blank" rel="noreferrer">
                        Privacy & Cookies
                    </a>
                </div>
            </div>
        );
    }

    if (screen === 'expired' && meta) {
        return <InviteExpiredCard invitationKey={key} />;
    }

    return null;
};

export default ValidateInvitePage;
